import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, Download, ImageIcon } from "lucide-react";
import { Helmet } from "react-helmet-async";

const CATALOGUE_URL = "/Prithira-Global-Catalogue-2026.pdf";

// All product images live in /public/products/ and ship with every deploy.
// `null` means "image not yet provided" — card renders a clean styled placeholder.
const categories = [
  {
    id: "bagasse",
    name: "Bagasse Tableware",
    tagline:
      "Made from sugarcane fibre. Strong, heat-resistant, microwave-safe, 100% compostable.",
    items: [
      {
        slug: "bagasse-round-plate",
        name: "Bagasse Round Plate",
        desc: 'Smooth, sturdy round plate. Available in 6", 8", 9", 10", 12". Ideal for meals, events, and takeaway.',
        image: "/products/bagasse-round-plate.jpg",
      },
      {
        slug: "bagasse-round-plate-3c",
        name: "Bagasse Round Plate — 4 Compartments",
        desc: 'Round plate with 4 sections. Available in 10" and 12". Ideal for full meals with sides.',
        image: "/products/bagasse-round-plate-3c.jpg",
      },
      {
        slug: "bagasse-round-plate-4c",
        name: "Bagasse Round Plate — 3 Compartments",
        desc: 'Round plate with 3 sections. Available in 9" and 10". Perfect for thali-style meals and canteen service.',
        image: "/products/bagasse-round-plate-4c.jpg",
      },
      {
        slug: "bagasse-round-bowl",
        name: "Bagasse Round Bowl",
        desc: 'Deep round bowl for soups, curries, and desserts. Available in 6", 8", 9".',
        image: "/products/bagasse-round-bowl.jpg",
      },
      {
        slug: "bagasse-round-bowl-lid",
        name: "Bagasse Round Bowl with Lid",
        desc: 'Secure-fit lid bowl for takeaway and delivery. Available in 6", 8", 9".',
        image: "/products/bagasse-round-bowl-lid.jpg",
      },
      {
        slug: "bagasse-sauce-cup",
        name: "Bagasse Cup with Lid",
        desc: "Small round container with separate lid. Ideal for sauces, chutneys, dips, soups, and desserts.",
        image: "/products/bagasse-sauce-cup.jpg",
      },
      {
        slug: "bagasse-square-tray",
        name: "Bagasse Square Tray",
        desc: 'Deep square tray for snacks and meals. Available in 6", 8", 9", 10".',
        image: "/products/bagasse-square-tray.jpg",
      },
      {
        slug: "bagasse-rect-tray-6c",
        name: "Bagasse Rectangular Meal Tray — 6 Compartment",
        desc: "Large rectangular tray with 6 compartments. Two sizes available. Ideal for institutional catering and airline-style service.",
        image: "/products/bagasse-rect-tray-6c.jpg",
      },
      {
        slug: "bagasse-rect-container-lid",
        name: "Bagasse Rectangular Container with Lid",
        desc: "Rectangular container with separate lid. Leak-resistant. Ideal for meal prep and delivery.",
        image: "/products/bagasse-rect-container-lid.jpg",
      },
      {
        slug: "bagasse-clamshell-small",
        name: "Bagasse Clamshell Box — Small",
        desc: "Compact single-compartment hinged box. Ideal for burgers, sandwiches, and snacks.",
        image: "/products/bagasse-clamshell-small.jpg",
      },
      {
        slug: "bagasse-clamshell-large",
        name: "Bagasse Clamshell Box — 3 Compartments",
        desc: "Hinged clamshell box with 3 internal compartments. Ideal for complete meal packaging.",
        image: "/products/bagasse-clamshell-large.jpg",
      },
      {
        slug: "bagasse-clamshell-3c",
        name: "Bagasse Box with Lid",
        desc: "Rectangular base with separate lid. Leak-resistant. Ideal for meal prep, takeaway, and delivery.",
        image: "/products/bagasse-clamshell-3c.jpg",
      },
      {
        slug: "bagasse-cup-lid",
        name: "Bagasse Beverage Cup with Lid",
        desc: "Compostable beverage cup with fitted domed lid. Suitable for hot and cold drinks.",
        image: "/products/bagasse-cup-lid.jpg",
      },
    ],
  },
  {
    id: "areca",
    name: "Areca Leaf Tableware",
    tagline:
      "Made from naturally fallen areca palm leaves. Chemical-free, biodegradable, rustic premium finish.",
    items: [
      {
        slug: "areca-square-plate-small",
        name: "Areca Leaf Square Plate — Small",
        desc: 'Natural square plate with leaf grain texture. Available in 6", 8".',
        image: "/products/areca-square-plate-small.jpg",
      },
      {
        slug: "areca-square-plate-large",
        name: "Areca Leaf Square Plate — Large",
        desc: 'Premium large square plate. Available in 9", 10", 12". Ideal for fine dining and premium hospitality.',
        image: "/products/areca-square-plate-large.jpg",
      },
      {
        slug: "areca-square-bowl",
        name: "Areca Leaf Square Deep Bowl",
        desc: 'Deep square bowl for rice, curries, and salads. Available in 6", 8", 9".',
        image: "/products/areca-square-bowl.jpg",
      },
      {
        slug: "areca-rect-tray",
        name: "Areca Leaf Rectangular Tray",
        desc: "Rectangular serving tray with natural texture. Available in multiple sizes. Ideal for starters and snacks.",
        image: "/products/areca-rect-tray.jpg",
      },
      {
        slug: "areca-oval-plate",
        name: "Areca Leaf Oval Plate",
        desc: 'Organic oval shape with natural colour variation. Available in 8", 10", 12". Each piece is unique.',
        image: "/products/areca-oval-plate.jpg",
      },
    ],
  },
  {
    id: "biobags",
    name: "Biodegradable Bags & Garbage Bags",
    tagline:
      "Made from cornstarch. 100% biodegradable and compostable. No plastic content.",
    items: [
      {
        slug: "biobag-13x16",
        name: "BioBag Cornstarch Carry Bag — 13×16",
        desc: "Printed compostable carry bag. Eco-friendly alternative to plastic shopping bags.",
        image: "/products/biobag-13x16.jpg",
      },
      {
        slug: "bio-carry-bag-16x20",
        name: "Cornstarch Carry Bag — 16×20",
        desc: "Plain white compostable carry bag for retail and grocery use.",
        image: "/products/bio-carry-bag-16x20.jpg",
      },
      {
        slug: "bio-carry-bag-32x36",
        name: "Cornstarch Carry Bag — 32×36",
        desc: "Large format compostable bag for bulk retail and packaging use.",
        image: "/products/bio-carry-bag-32x36.jpg",
      },
      {
        slug: "garbage-bag-green",
        name: "Wet Waste Garbage Bag — Green (19×22)",
        desc: "Colour-coded green for wet/biodegradable waste. Made from cornstarch. As per Swachh Bharat waste segregation guidelines. 30 bags per roll, 15L capacity.",
        image: "/products/garbage-bag-green.jpg",
      },
      {
        slug: "garbage-bag-blue",
        name: "Dry Waste Garbage Bag — Blue (19×22)",
        desc: "Colour-coded blue for dry waste. Made from cornstarch. As per Swachh Bharat waste segregation guidelines. 30 bags per roll, 15L capacity.",
        image: "/products/garbage-bag-blue.jpg",
      },
    ],
  },
  {
    id: "cutlery",
    name: "Bio Cutlery & Natural Scrubbers",
    tagline:
      "Plant-based cutlery and natural coconut fibre scrubbers — plastic-free, biodegradable, ready for bulk supply.",
    items: [
      {
        slug: "bio-cutlery",
        name: "Bio Cutlery Set",
        desc: "Fork, spoon, and knife made from cornstarch / wood. Plastic-free, compostable. Suitable for all food service occasions.",
        image: "/products/bio-cutlery.jpg",
      },
      {
        slug: "coir-scrubber",
        name: "Coconut Coir Scrubber",
        desc: "100% natural coconut fibre scrubber. No microplastics. Durable and fully biodegradable.",
        image: "/products/coir-scrubber.jpg",
      },
    ],
  },
];

// Flatten for SEO schema
const allItems = categories.flatMap((c) =>
  c.items.map((i) => ({ ...i, category: c.name }))
);

const productsItemListSchema = {
  "@context": "https://schema.org",
  "@type": "ItemList",
  name: "Prithira Global — Biodegradable Tableware & Packaging Catalogue",
  url: "https://www.prithiraglobal.com/products",
  numberOfItems: allItems.length,
  itemListElement: allItems.map((p, idx) => ({
    "@type": "ListItem",
    position: idx + 1,
    item: {
      "@type": "Product",
      "@id": `https://www.prithiraglobal.com/products#${p.slug}`,
      name: p.name,
      description: p.desc,
      category: p.category,
      ...(p.image
        ? { image: `https://www.prithiraglobal.com${p.image}` }
        : {}),
      brand: { "@type": "Brand", name: "Prithira Global" },
      manufacturer: { "@type": "Organization", name: "Prithira Global" },
      url: `https://www.prithiraglobal.com/products#${p.slug}`,
      offers: {
        "@type": "Offer",
        availability: "https://schema.org/InStock",
        price: "1",
        priceCurrency: "INR",
        priceSpecification: {
          "@type": "PriceSpecification",
          priceCurrency: "INR",
          description: "Contact for bulk pricing",
        },
        seller: { "@type": "Organization", name: "Prithira Global" },
      },
    },
  })),
};

const ProductCard = ({ item }) => (
  <article
    id={item.slug}
    data-testid={`product-card-${item.slug}`}
    className="group bg-white border border-stone-200 rounded-2xl overflow-hidden flex flex-col scroll-mt-24 hover:border-primary/30 hover:shadow-lg transition-all duration-300"
  >
    <div className="aspect-[4/3] bg-stone-100 overflow-hidden relative">
      {item.image ? (
        <img
          src={item.image}
          alt={item.name}
          loading="lazy"
          className="w-full h-full object-contain p-3 transition-transform duration-700 group-hover:scale-105"
        />
      ) : (
        <div
          className="w-full h-full flex flex-col items-center justify-center text-primary/40 gap-2"
          data-testid={`product-placeholder-${item.slug}`}
        >
          <ImageIcon className="w-10 h-10" strokeWidth={1.5} />
          <span className="text-[10px] uppercase tracking-widest font-medium">
            Image coming soon
          </span>
        </div>
      )}
    </div>
    <div className="p-5 flex flex-col flex-1">
      <h3 className="font-heading text-lg text-stone-900 mb-2 leading-snug">
        {item.name}
      </h3>
      <p className="text-sm text-stone-600 leading-relaxed mb-5 flex-1">
        {item.desc}
      </p>
      <Link
        to="/contact"
        data-testid={`enquire-${item.slug}`}
        className="inline-flex items-center justify-center gap-1.5 self-start text-sm font-medium text-primary border border-primary/40 rounded-full px-5 py-2 hover:bg-primary hover:text-white transition-colors"
      >
        Enquire Now
        <ArrowRight className="h-3.5 w-3.5" />
      </Link>
    </div>
  </article>
);

const ProductsPage = () => {
  // Inject ItemList JSON-LD into <head> on mount.
  // Bulletproof for both runtime and react-snap prerender (waits 2s after mount).
  useEffect(() => {
    const id = "products-itemlist-schema";
    let el = document.getElementById(id);
    if (!el) {
      el = document.createElement("script");
      el.id = id;
      el.type = "application/ld+json";
      document.head.appendChild(el);
    }
    el.textContent = JSON.stringify(productsItemListSchema);
    return () => {
      const existing = document.getElementById(id);
      if (existing) existing.remove();
    };
  }, []);

  return (
    <div data-testid="products-page" className="noise-overlay">
      <Helmet
        title="Our Products — Biodegradable Tableware & Packaging | Prithira Global"
        meta={[
          {
            name: "description",
            content:
              "Premium biodegradable tableware and packaging — bagasse plates, bowls, trays, areca leaf tableware, cornstarch carry bags, garbage bags, bio cutlery, and coconut coir scrubbers. Export-ready, available in bulk.",
          },
          {
            name: "keywords",
            content:
              "bagasse tableware supplier India, areca leaf plates exporter, cornstarch carry bags, compostable garbage bags, bio cutlery, coconut coir scrubber, biodegradable packaging wholesale",
          },
          {
            property: "og:title",
            content: "Our Products | Prithira Global",
          },
          {
            property: "og:description",
            content:
              "Bagasse, areca leaf, cornstarch bags, bio cutlery, coconut scrubbers — premium biodegradable tableware, responsibly sourced and export-ready.",
          },
          {
            property: "og:url",
            content: "https://www.prithiraglobal.com/products",
          },
        ]}
        link={[
          {
            rel: "canonical",
            href: "https://www.prithiraglobal.com/products",
          },
        ]}
      />

      {/* Hero */}
      <section className="pt-32 pb-16 bg-sand-200/30">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <span className="caption text-primary mb-4 block">Our Catalogue</span>
            <h1 className="heading-xl text-stone-900 mb-6">
              Our <span className="italic">Products</span>
            </h1>
            <p className="body-lg text-stone-600">
              Premium biodegradable tableware and packaging — responsibly
              sourced, export-ready, available in bulk.
            </p>
            <a
              href={CATALOGUE_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="products-hero-catalogue-btn"
              className="inline-flex items-center gap-2 mt-8 text-sm font-medium text-primary border border-primary/40 rounded-full px-6 py-2.5 hover:bg-primary hover:text-white transition-colors"
            >
              <Download className="h-4 w-4" />
              Download Full Catalogue (PDF)
            </a>
          </div>
        </div>
      </section>

      {/* Categories */}
      {categories.map((cat, idx) => (
        <section
          key={cat.id}
          id={cat.id}
          data-testid={`category-${cat.id}`}
          className={`section-spacing scroll-mt-24 ${
            idx % 2 === 0 ? "bg-white" : "bg-sand-200/30"
          }`}
        >
          <div className="container-custom">
            <div className="max-w-3xl mb-12">
              <h2 className="heading-lg text-stone-900 mb-3">{cat.name}</h2>
              <p className="text-base italic text-stone-500 leading-relaxed">
                {cat.tagline}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
              {cat.items.map((item) => (
                <ProductCard key={item.slug} item={item} />
              ))}
            </div>
          </div>
        </section>
      ))}

      {/* Pricing */}
      <section
        data-testid="products-pricing-section"
        className="section-spacing bg-white border-t border-stone-100"
      >
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <span className="caption text-primary mb-4 block">Pricing</span>
            <h2 className="heading-lg text-stone-900 mb-8">
              Priced for <span className="italic">Bulk B2B Supply</span>
            </h2>
            <p className="body-lg text-stone-600 font-light leading-relaxed">
              We supply biodegradable packaging products across a wide range of
              price points depending on product type, size, quantity and order
              volume. Our products are priced for bulk B2B supply — typically
              ranging from <strong className="text-stone-900 font-medium">₹1.50 per piece</strong> for
              standard bagasse plates to <strong className="text-stone-900 font-medium">₹17 per piece</strong> for
              premium containers with lids. Cornstarch carry bags are priced
              from <strong className="text-stone-900 font-medium">₹190 per kg</strong> onwards.
              For an accurate quotation based on your specific requirements and
              volume, please <Link to="/contact" className="text-primary hover:underline font-medium">contact us directly</Link>.
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-spacing bg-primary text-white">
        <div className="container-custom text-center">
          <h2 className="heading-lg mb-6">
            Need <span className="italic">Bulk Pricing</span> or Custom Sizing?
          </h2>
          <p className="body-lg text-white/80 mb-10 max-w-2xl mx-auto">
            We supply institutional buyers, exporters and distributors at scale.
            Share your requirements and our team will respond within 24 hours.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href={CATALOGUE_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="products-footer-catalogue-btn"
            >
              <Button className="rounded-full px-8 py-6 text-base font-medium bg-white text-primary hover:bg-stone-100 btn-lift">
                <Download className="mr-2 h-5 w-5" />
                Download Catalogue
              </Button>
            </a>
            <Link to="/contact">
              <Button
                data-testid="products-footer-cta-btn"
                variant="outline"
                className="rounded-full px-8 py-6 text-base font-medium border-2 border-white text-white hover:bg-white/10"
              >
                Enquire Now
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ProductsPage;
